import React, { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour, Stethoscope, Drop, Coin, Sparkle, SignOut, List, Package, Pill, ChartLineUp, Plant, ChefHat,
  UsersThree, ShieldCheck, CloudArrowDown, ClockCounterClockwise, CaretDown,
  Syringe, Heartbeat, Baby, FirstAidKit, CalendarCheck, Medal, ChartBar, ArrowLineRight,
  ListChecks, ChartLine, Robot, ClipboardText, Receipt, UserCircle, Leaf,
} from "@phosphor-icons/react";

// Açılır alt menüler (yalnızca Veteriner, İlaçlar, Stok, Finans)
const SUBMENUS = {
  veteriner: [
    { tab: "inseminations", label: "Tohumlama", icon: Syringe },
    { tab: "pregnancy", label: "Gebelik Kontrolü", icon: Heartbeat },
    { tab: "births", label: "Doğum", icon: Baby },
    { tab: "treatments", label: "Tedavi", icon: FirstAidKit },
    { tab: "vaccines", label: "Aşı Takvimi", icon: CalendarCheck },
    { tab: "performance", label: "Personel Karnesi", icon: Medal },
    { tab: "stats", label: "İstatistikler", icon: ChartBar },
    { tab: "herd-exit", label: "Sürüden Çıkış", icon: ArrowLineRight },
  ],
  ilaclar: [
    { tab: "stock", label: "İlaç Stoğu", icon: Package },
    { tab: "protocols", label: "Tedavi Protokolleri", icon: ListChecks },
    { tab: "semen", label: "Sperma Stoğu", icon: Drop },
    { tab: "counts", label: "Aylık Sayım", icon: ListChecks },
    { tab: "usage", label: "Kullanım Kaydı", icon: Pill },
    { tab: "stats", label: "İstatistikler", icon: ChartLine },
  ],
  stok: [
    { tab: "items", label: "Stok Kalemleri", icon: Package },
    { tab: "lely", label: "Lely Robot", icon: Robot },
    { tab: "counts", label: "Sayım Geçmişi", icon: ClipboardText },
    { tab: "reports", label: "Raporlar", icon: ChartLine },
  ],
  finans: [
    { tab: "records", label: "Gelir/Gider Kayıtları", icon: Coin },
    { tab: "payables", label: "Ödemeler", icon: Receipt },
    { tab: "personnel", label: "Personel", icon: UserCircle },
    { tab: "feed", label: "Yem Analizi", icon: Leaf },
  ],
};

const NAV = [
  { to: "/", label: "Panel", icon: SquaresFour, testId: "nav-dashboard", exact: true, key: "panel" },
  { to: "/veteriner", label: "Veteriner", icon: Stethoscope, testId: "nav-vet", key: "veteriner", children: SUBMENUS.veteriner },
  { to: "/ilaclar", label: "İlaçlar", icon: Pill, testId: "nav-medicines", key: "ilaclar", children: SUBMENUS.ilaclar },
  { to: "/buzagi", label: "Buzağı Besleme", icon: Baby, testId: "nav-calf", key: "buzagi" },
  { to: "/sut", label: "Süt Üretimi", icon: Drop, testId: "nav-milk", key: "sut" },
  { to: "/stok", label: "Stok", icon: Package, testId: "nav-stock", key: "stok", children: SUBMENUS.stok },
  { to: "/tarla", label: "Tarla", icon: Plant, testId: "nav-fields", key: "tarla" },
  { to: "/rasyon", label: "Rasyon", icon: ChefHat, testId: "nav-rations", key: "rasyon" },
  { to: "/suru", label: "Sürü Durumu", icon: UsersThree, testId: "nav-herd", key: "suru" },
  { to: "/finans", label: "Finans", icon: Coin, testId: "nav-finance", key: "finans", children: SUBMENUS.finans },
  { to: "/karsilastirma", label: "Karşılaştırma", icon: ChartLineUp, testId: "nav-comparison", key: "karsilastirma" },
  { to: "/ai", label: "AI Danışman", icon: Sparkle, testId: "nav-ai", key: "ai" },
];

// Akordiyon grup başlığı + alt menü
function NavGroup({ item, mobile, currentPath, currentTab, onNavigate }) {
  const Icon = item.icon;
  const isOnPage = currentPath === item.to;
  const [expanded, setExpanded] = useState(isOnPage);
  useEffect(() => { if (isOnPage) setExpanded(true); }, [isOnPage]);

  const effectiveTab = currentTab || item.children[0].tab;
  const prefix = mobile ? "m-" : "";

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`${prefix}${item.testId}`}
        aria-expanded={expanded}
        className={`nav-item w-full text-left ${isOnPage ? "nav-group-active" : ""}`}
      >
        <Icon size={20} weight="duotone" />
        <span className="flex-1">{item.label}</span>
        <CaretDown size={15} weight="bold" className={`nav-caret ${expanded ? "open" : ""}`} />
      </button>
      {expanded && (
        <div className="nav-sub" data-testid={`${prefix}submenu-${item.key}`}>
          {item.children.map((ch) => {
            const active = isOnPage && effectiveTab === ch.tab;
            const SubIcon = ch.icon;
            return (
              <Link
                key={ch.tab}
                to={{ pathname: item.to, search: `?t=${ch.tab}` }}
                onClick={onNavigate}
                data-testid={`${prefix}${item.testId}-${ch.tab}`}
                className={`nav-subitem ${active ? "active" : ""}`}
              >
                <SubIcon size={16} weight="duotone" />
                <span>{ch.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/login"); };

  const currentPath = location.pathname;
  const currentTab = new URLSearchParams(location.search).get("t") || "";

  const allowed = Array.isArray(user?.allowed_pages) ? user.allowed_pages : [];
  const isOwner = user?.role === "owner";
  const visibleNav = NAV.filter((n) => allowed.includes(n.key));
  const ownerExtras = isOwner
    ? [
        { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldCheck, testId: "nav-users", key: "kullanicilar" },
        { to: "/yedekleme", label: "Yedekleme", icon: CloudArrowDown, testId: "nav-backup", key: "yedekleme" },
        { to: "/denetim", label: "Denetim İzi", icon: ClockCounterClockwise, testId: "nav-audit", key: "denetim" },
      ]
    : [];

  const renderNav = (mobile = false) =>
    [...visibleNav, ...ownerExtras].map((item) => {
      const closeOnNav = mobile ? () => setOpen(false) : undefined;
      if (item.children && item.children.length) {
        return (
          <NavGroup
            key={item.to}
            item={item}
            mobile={mobile}
            currentPath={currentPath}
            currentTab={currentTab}
            onNavigate={closeOnNav}
          />
        );
      }
      const Icon = item.icon;
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.exact}
          onClick={closeOnNav}
          data-testid={mobile ? `m-${item.testId}` : item.testId}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
        >
          <Icon size={20} weight="duotone" /> <span>{item.label}</span>
        </NavLink>
      );
    });

  return (
    <div className="min-h-screen flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#E5E2DC] bg-white h-screen sticky top-0">
        <div className="p-4 border-b border-[#E5E2DC] flex items-center justify-center shrink-0">
          <img src="/seher-logo.png" alt="Seher Tarım ve Hayvancılık" className="max-h-14 w-auto object-contain" />
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {renderNav(false)}
        </nav>
        <div className="p-3 border-t border-[#E5E2DC] shrink-0">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-[#8a938c] truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1 text-[#417251]">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="nav-item w-full text-left">
            <SignOut size={20} weight="duotone" /> <span>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E5E2DC] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/seher-logo.png" alt="Seher" className="h-9 w-auto object-contain" />
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="p-2.5 -mr-1"><List size={24} /></button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-xs bg-white p-4 pt-16 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="space-y-1">{renderNav(true)}</div>
            <button onClick={doLogout} className="nav-item w-full text-left mt-2">
              <SignOut size={20} weight="duotone" /> <span>Çıkış Yap</span>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">
        <div className="p-4 md:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
